import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { fetchBalanceSheetStatementData } from "../../apis/Stock";
import TotalAssetRatioChart from "../chart/TotalAssetRatioChart";
import CashAndShortTermInvestmentsChart from "../chart/CashAndShortTermInvestmentsChart";
import CurrentRationChart from "../chart/CurrentRationChart";
import TotalLiabilitiesChart from "../chart/TotalLiabilitiesChart";
import LiabilitiesRationChart from "../chart/LiabilitiesRationChart";
import LiabilitiesEquityLineChart from "../chart/LiabilitiesEquityLineChart";
import ReceivablesPayablesLineChart from "../chart/ReceivablesPayablesLineChart";
import InventoryLineChart from "../chart/InventoryLineChart";
import RetainedEarningsLineChart from "../chart/RetainedEarningsLineChart";

export default function BalanceSheetChart({ ticker }) {
  const [selectedPeriod, setSelectedPeriod] = useState("quarter");
  const [isLoading, setIsLoading] = useState(false);
  const [chartDataByPeriod, setChartDataByPeriod] = useState({ quarter: null, annual: null });

  useEffect(() => {
    if (!ticker) {
      setChartDataByPeriod({ quarter: null, annual: null });
      setIsLoading(false);
      return;
    }

    setChartDataByPeriod({ quarter: null, annual: null });
  }, [ticker]);

  useEffect(() => {
    if (!ticker) {
      return;
    }

    if (chartDataByPeriod[selectedPeriod] !== null) {
      return;
    }

    let isMounted = true;

    async function fetchData() {
      setIsLoading(true);

      try {
        const response = await fetchBalanceSheetStatementData(ticker, selectedPeriod);

        if (!response.ok) {
          throw new Error("Failed to fetch balance sheet statement");
        }

        const data = await response.json();
        if (!isMounted) return;

        setChartDataByPeriod((prev) => ({
          ...prev,
          [selectedPeriod]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        console.error("Error fetching balance sheet statement:", error);
        if (isMounted) {
          setChartDataByPeriod((prev) => ({
            ...prev,
            [selectedPeriod]: [],
          }));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [chartDataByPeriod, selectedPeriod, ticker]);

  return (
    <div className="w-100 h-100" style={{ minHeight: "300px" }}>
      <div className="d-flex justify-content-start align-items-center mb-2 ps-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="재무상태표 차트 기간 선택">
          <button
            type="button"
            className={`btn ${selectedPeriod === "quarter" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setSelectedPeriod("quarter")}
          >
            분기
          </button>
          <button
            type="button"
            className={`btn ${selectedPeriod === "annual" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setSelectedPeriod("annual")}
          >
            연간
          </button>
        </div>
      </div>
      <Row style={{ margin: "0 auto" }}>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          {/* 자산 구성 비중 */}
          <TotalAssetRatioChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          {/*  현금성 자산 및 단기 투자금 */}
          <CashAndShortTermInvestmentsChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          {/* 유동성 비율 */}
          <CurrentRationChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
      </Row>
      <Row style={{ margin: "0 auto" }}>
        <Col xs={12} sm={12} md={12} lg={4} className="mb-2">
          {/* 부채구성내역 */}
          <TotalLiabilitiesChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} className="mb-2">
          {/* 부채비율  */}
          <LiabilitiesRationChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} className="mb-2">
          {/* 자본과 부채 트렌드 */}
          <LiabilitiesEquityLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
      </Row>
      <Row style={{ margin: "0 auto" }}>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          {/* 매출채권 vs 매입채무 */}
          <ReceivablesPayablesLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          {/* 재고 자산 비중 */}
          <InventoryLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          {/* 이익 잉여금 현황 */}
          <RetainedEarningsLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
      </Row>
    </div>
  );
}
